import { z } from "zod"

import {
  branchIdSchema,
  correlationIdSchema,
  entityIdSchema,
  legalEntityIdSchema,
} from "./identifiers.js"
import { fleetAssetKindV1Schema, fleetOperatingStatusV1Schema } from "./fleet-asset-v1.js"

const inspectionCheckV1Schema = z.object({
  checkKey: z.string().regex(/^[a-z][a-z0-9_]{1,63}$/),
  label: z.string().trim().min(2).max(140),
  safetyCritical: z.boolean(),
  evidenceRequiredOnFailure: z.boolean(),
}).strict()

export const fleetInspectionTemplatePublishV1Schema = z.object({
  requestVersion: z.literal(1),
  correlationId: correlationIdSchema,
  idempotencyKey: z.string().regex(/^[A-Za-z0-9][A-Za-z0-9._:-]{0,127}$/),
  templateId: entityIdSchema,
  templateVersionId: entityIdSchema,
  legalEntityId: legalEntityIdSchema,
  branchId: branchIdSchema,
  expectedVersion: z.literal(0),
  name: z.string().trim().min(2).max(140),
  assetKind: fleetAssetKindV1Schema,
  effectiveFrom: z.string().datetime({ offset: true }),
  effectiveTo: z.string().datetime({ offset: true }).nullable(),
  checks: z.array(inspectionCheckV1Schema).min(1).max(64),
}).strict().superRefine((value, context) => {
  if (new Set(value.checks.map((check) => check.checkKey)).size !== value.checks.length) {
    context.addIssue({ code: "custom", message: "Inspection check keys must be unique." })
  }
})

const inspectionResponseV1Schema = z.object({
  checkKey: z.string(),
  outcome: z.enum(["pass", "fail"]),
  evidenceRef: z.string().trim().min(3).max(256).nullable(),
  notes: z.string().trim().max(512).nullable(),
}).strict()

export const fleetInspectionRecordV1Schema = z.object({
  requestVersion: z.literal(1),
  correlationId: correlationIdSchema,
  idempotencyKey: z.string().regex(/^[A-Za-z0-9][A-Za-z0-9._:-]{0,127}$/),
  inspectionId: entityIdSchema,
  assetId: entityIdSchema,
  templateVersionId: entityIdSchema,
  assetRevisionId: entityIdSchema,
  statusEventId: entityIdSchema,
  followUpId: entityIdSchema,
  legalEntityId: legalEntityIdSchema,
  branchId: branchIdSchema,
  expectedVersion: z.number().int().positive(),
  workerId: entityIdSchema,
  visitId: entityIdSchema.nullable(),
  meterMilli: z.number().int().nonnegative(),
  performedAt: z.string().datetime({ offset: true }),
  responses: z.array(inspectionResponseV1Schema).min(1).max(64),
}).strict()

export const fleetInspectionResultV1Schema = z.object({
  entityType: z.literal("fleet_asset"),
  entityId: entityIdSchema,
  state: fleetOperatingStatusV1Schema,
  version: z.number().int().positive(),
  inspectionId: entityIdSchema,
  templateVersionId: entityIdSchema,
  safetyCriticalFailed: z.boolean(),
  operatingStatus: fleetOperatingStatusV1Schema,
  followUpId: entityIdSchema.nullable(),
}).strict()

export type FleetInspectionTemplatePublishV1 = z.infer<
  typeof fleetInspectionTemplatePublishV1Schema
>
export type FleetInspectionRecordV1 = z.infer<typeof fleetInspectionRecordV1Schema>
export type FleetInspectionResultV1 = z.infer<typeof fleetInspectionResultV1Schema>
